import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ProfileCompletionModal } from "@/components/ProfileCompletionModal";

interface PlayerAuthGuardProps {
  children: React.ReactNode;
}

const PlayerAuthGuard = ({ children }: PlayerAuthGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [userId, setUserId] = useState("");
  const [userEmail, setUserEmail] = useState("");
  
  // Extract clientSlug from current path
  const pathParts = location.pathname.split('/');
  const clientSlug = pathParts[1] === 'c' ? pathParts[2] : 'default';
  const playerAuthPath = `/c/${clientSlug}/player-auth`;

  useEffect(() => {
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!session) {
          navigate(playerAuthPath, { state: { from: location.pathname } });
        } else {
          setAuthenticated(true);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate, location.pathname, playerAuthPath]);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate(playerAuthPath, { state: { from: location.pathname } });
      } else {
        setAuthenticated(true);
        setUserId(session.user.id);
        setUserEmail(session.user.email || "");
        
        // Check if profile needs phone completion
        const { data: profile } = await supabase
          .from("profiles")
          .select("phone")
          .eq("id", session.user.id)
          .single();
        
        // Show modal if phone is null (never set)
        // phone === "" means user skipped, phone === null means first time
        if (profile && profile.phone === null) {
          setShowProfileCompletion(true);
        }
      }
    } catch (error) {
      console.error("Auth check error:", error);
      navigate(playerAuthPath, { state: { from: location.pathname } });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return null;
  }

  return (
    <>
      {children}
      <ProfileCompletionModal
        isOpen={showProfileCompletion}
        userId={userId}
        userEmail={userEmail}
        onComplete={() => setShowProfileCompletion(false)}
      />
    </>
  );
};

export default PlayerAuthGuard;
